import { Box, CircularProgress } from '@material-ui/core';
import classnames from 'classnames';
import SGSButton from 'components/SGSButton';
import SGSHelpButton from 'components/SGSHelpButton';
import NotificationQueue from 'features/notification/components/SGSNotification';
import { UserWelcomePage } from 'pages';
import UsersPageAdd from 'pages/Utilisateurs/Add';
import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { matchPath, useHistory, useLocation } from 'react-router';
import IconAssures from '../../assets/assures.svg';
import IconContact from '../../assets/contact.svg';
import IconCotisations from '../../assets/cotisations.svg';
import IconDocumentation from '../../assets/documentation.svg';
import IconEspacePrive from '../../assets/espace-prive.svg';
import IconGaranties from '../../assets/garanties.svg';
import IconHelp from '../../assets/help.svg';
import IconStatistiques from '../../assets/statistiques.svg';
import IconAccesUtilisateurs from '../../assets/utilisateur.svg';
import { useAppContext, useDrawerContext } from '../../core/contexts';
import { Menu, routes } from '../../core/models';
import { iframe } from '../../core/utils';
import { setFrameUrl } from '../../core/utils/iframe';
import AppLoader from '../AppLoaders';
import SGSPageTitle from '../SGSPageTitle';
import Footer from './Footer';
import Header from './Header';
import styles from './Layout.module.scss';
import MaintenancePage from './Maintenance/Maintenance';
import Sidebar from './Sidebar';

const TRACKING_ID = process.env.REACT_APP_TRACKING_ID; // OUR_TRACKING_ID
if (TRACKING_ID !== '') ReactGA.initialize(TRACKING_ID);

const { isRefused, initIframe, hideLayout, menuClick } = iframe;

type Props = {
    children: React.ReactNode;
};

const Layout: React.FC<Props> = ({ children }) => {
    const {
        location: { pathname },
    } = useHistory();
    const { t } = useTranslation();
    const { token, user, setToken, setAppLoading, iframeLoading, setIframeLoading, logout } = useAppContext();
    const { drawerOpen } = useDrawerContext();
    const history = useHistory();
    const location = useLocation();
    const [link, setLink] = useState('');
    // const [wantMyAccount, setWantMyAccount] = useState<boolean>(false);
    const [iframeReady, setIframeReady] = useState<boolean>(false);
    const [isHidden, setIsHidden] = useState(true);
    const [displayed, setDisplayed] = useState<boolean>(false);
    const currentMenu = useRef(null);
    // When we replace the iframe, change to generate menu from routes.ts object.
    const menus: Menu[] = [
        {
            name: t('privateSpace.label'),
            icon: IconEspacePrive,
            path: '/',
        },
        // {
        //     name: 'HomePageNew',
        //     icon: IconEspacePrive,
        //     role: 'none',
        //     path: '/homepage-new',
        // },
        {
            name: t('userAccess.label'),
            icon: IconAccesUtilisateurs,
            role: ['ROLE_ADD_USER', 'ROLE_EDIT_USER', 'ROLE_LIST_USER'],
            collapsed: !drawerOpen || !pathname.startsWith('/utilisateurs') || pathname === '/utilisateurs/modifier',
            menuItems: [
                {
                    name: t('userAccess.addUser.label'),
                    role: ['ROLE_ADD_USER'],
                    path: '/utilisateurs/ajouter',
                },
                {
                    name: t('userAccess.users.label'),
                    role: ['ROLE_EDIT_USER', 'ROLE_LIST_USER'],
                    path: '/utilisateurs',
                },
            ],
        },
        {
            name: user.num_adherent === 'XX9904752' ? 'Mes salariés intérimaires' : t('affiliations.label'),
            icon: IconAssures,
            collapsed: !drawerOpen || !(pathname.startsWith('/affiliations') || pathname.startsWith('/arrets') || pathname.startsWith('/radiations')),
            menuItems: [
                {
                    name: t('affiliations.affiliates.label'),
                    role: ['ROLE_HISTORY_ASSURED'],
                    path: '/affiliations',
                    features: ['SANTE', 'PREV'],
                },
                {
                    name: t('affiliations.contrat.label'),
                    role: ['ROLE_SEE_BIA'],
                    path: '/affiliations/bias',
                    features: ['BIA'],
                },
                // {
                //     name: t('affiliations.rtt.list.label'),
                //     role: ['ROLE_ADD_WORK_STOPPING'],
                //     path: '/arrets/create',
                // },
                {
                    name: 'Déclarer un arrêt de travail',
                    role: ['ROLE_ADD_WORK_STOPPING'],
                    path: '/arrets/home',
                },

                {
                    name: t('rtt.declare.label'),
                    role: ['ROLE_ADD_WORK_STOPPING'],
                    iframeId: 'c-4-A20', // c-5-A20
                    path: '/arrets/create',
                    menuTxt: "Déclaration d'arrêt de travail",
                    features: ['PREV'],
                },
                {
                    name: t('compensation.affiliates.label'),
                    role: ['ROLE_DOSSIER_PREV'],
                    path: '/affiliations/indemnisation',
                    features: ['PREV'],
                },
                // {
                //     name: t('incapacity.declaration.label'),
                //     role: ['ROLE_DOSSIER_PREV'],
                //     path: '/affiliations/incapacity',
                // },
                // {
                //     name: t('demise.declaration.label'),
                //     role: ['ROLE_DOSSIER_PREV'],
                //     path: '/affiliations/demise',
                // },
            ],
        },
        {
            name: t('contract.label'),
            icon: IconDocumentation,
            collapsed: !pathname.startsWith('/contract'),
            menuItems: [
                {
                    name: t('contract.documentation.label'),
                    path: '/contract/documentation',
                    role: ['ROLE_DOCUMENTATION'],
                },
            ],
        },

        {
            name: t('cotisations.label'),
            icon: IconCotisations,
            collapsed: !drawerOpen || !pathname.startsWith('/cotisations'),
            menuItems: [
                {
                    name: t('cotisations.call.label'),
                    role: ['ROLE_CONTRIBUTION_SEE'],
                    path: '/cotisations',
                },
            ],
        },
        {
            name: t('statistics.label'),
            icon: IconStatistiques,
            role: ['ROLE_RT_STATISTIC', 'ROLE_STATISTIC'],
            collapsed: !drawerOpen || !pathname.startsWith('/Statistiques'),
            path: '/Statistiques',
            features: ['SANTE'],
        },

        {
            name: t('informations.label'),
            icon: IconGaranties,
            collapsed: !drawerOpen || !(pathname.startsWith('/actualites') || pathname.startsWith('/decrypt_rhs')),
            menuItems: [
                {
                    name: t('informations.actualites.label'),
                    path: '/actualites',
                },
                {
                    name: t('informations.decrypt_rhs.label'),
                    path: '/decrypt_rhs',
                },
            ],
        },
        {
            name: t('help.label'),
            icon: IconHelp,
            collapsed: !drawerOpen || !(pathname.startsWith('/help') || pathname.startsWith('/faq') || pathname.startsWith('/tutos')),
            menuItems: [
                {
                    name: t('help.guide.label'),
                    path: '/help/guide',
                },
                {
                    name: t('help.faq.label'),
                    path: '/faq',
                },
                {
                    name: t('help.tutos.label'),
                    path: '/tutos',
                },
            ],
        },

        {
            name: t('contact.label'),
            icon: IconContact,
            collapsed: true,
            path: '/contact',
            role: ['ROLE_CONTACT'],
        },
    ];

    // Redirect new user to inscription
    if (user.roles.includes('ROLE_NEW_INSCRIPTION') && location.pathname !== '/utilisateurs/bienvenue' && location.pathname !== '/utilisateurs/ajouter') {
        history.push('/utilisateurs/bienvenue');
    }

    const currentPage = routes.find((o) => {
        const r = matchPath(history.location.pathname, {
            exact: true,
            path: o.path,
        });
        return r ? (o.iframeId ? null : o) : null;
    });

    const handleLoad = () => {
        // Get Iframe & iframe document
        const iframe: any = document.getElementById('iframe-sgsante');
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Get iframe parent loader
        const loader = document.getElementById('loadingPage');
        const loaiFrameLoaderder = document.getElementById('iFrameLoader');

        // Hide loader
        const hideIframe = () => {
            loader.style.display = 'block';
            iframe.style.visibility = 'hidden';
            loaiFrameLoaderder.style.display = 'block';
        };

        // Show loader
        const displayLoader = () => {
            loader.style.display = 'block';
        };

        // display loader on frame unload
        iframe.contentWindow.onunload = function () {
            hideIframe();
        };

        // display loader click to link, next step or element taht hava a link as parent
        iframeDoc.addEventListener(`click`, (e) => {
            const el = e.target;
            const elContent = el?.firstChild?.nodeValue ? el.firstChild.nodeValue : null;
            const parentLevel1 = el?.parentElement ? el.parentElement : null;
            const parentLevel2 = el?.parentElement?.parentElement ? el.parentElement?.parentElement : null;

            if (el.tagName.toLowerCase() === 'a') {
                displayLoader();
            } else if (elContent && (elContent.includes('Précédente') || elContent.includes('Suivante'))) {
                displayLoader();
            } else if (parentLevel1 && parentLevel1.tagName.toLowerCase() === 'a') {
                displayLoader();
            } else if (parentLevel2 && parentLevel2.tagName.toLowerCase() === 'a') {
                displayLoader();
            }
        });

        // Is user deconnected ?
        const divElements = iframeDoc.querySelectorAll('div');
        const isDeconnectedDiv = Array.from(divElements).find((o: any) => o?.textContent.includes('Vous avez été déconnecté') || o?.textContent.includes("La session n'existe plus"));
        if (isDeconnectedDiv) {
            logout();
            window.location.reload();
        }

        try {
            setIframeReady(false);
            initIframe(setAppLoading);

            const route = routes.find((o) => o.path === location.pathname);
            if (!route && isRefused()) {
                setToken('');
                localStorage.removeItem('iframeUrl');
            } else {
                hideLayout();
            }
        } catch (err) {
            console.error(err);
        } finally {
            // Get correct iframe menu
            if (currentMenu.current == null) {
                routes
                    .filter((item) => item.iframeId)
                    .forEach((item) => {
                        if (item.path === history.location.pathname) {
                            currentMenu.current = item.iframeId;
                        }
                    });
            }
            setIframeReady(true);
            setAppLoading(false);
            setIframeLoading(false);
        }
    };

    const handleRedirect = (menu: Menu) => {
        if (!currentPage && menu.iframeId && currentMenu.current === menu.iframeId) {
            return;
        }
        setDisplayed(false);
        currentMenu.current = menu.iframeId;
        if (menu.path) {
            history.push(menu.path);
        }
        if (!menu.iframeId) {
            setIsHidden(true);
        } else {
            setIsHidden(false);
            setIframeLoading(true);
            if (iframeReady) {
                menuClick(currentMenu.current, isHidden, menu.menuTxt).then(() => {
                    setAppLoading(false);
                    setIframeLoading(false);
                });
            }
        }
    };

    const getIfIframeIsHidden = () => {
        setIsHidden(!!currentPage);
    };

    useEffect(() => {
        if (iframeReady) {
            menuClick(currentMenu.current, isHidden).then(() => {
                setAppLoading(false);
                setIframeLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iframeReady]);

    useEffect(() => {
        getIfIframeIsHidden();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    useEffect(() => {
        if (TRACKING_ID !== '') ReactGA.send({ hitType: 'pageview', page: location.pathname });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        const { winDebToken: oldSiteToken } = user;
        if (oldSiteToken) {
            let newLink: any = `${process.env.REACT_APP_FRAME_URL}${oldSiteToken}`;
            if (localStorage.getItem('iframeUrl')) {
                newLink = localStorage.getItem('iframeUrl');
            }
            setLink(newLink);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        getIfIframeIsHidden();
        if (!!currentPage) {
            setIframeLoading(false);
            setAppLoading(false);
        } else {
            setIframeLoading(true);
        }
        window.addEventListener('beforeunload', () => {
            setFrameUrl();
        });
        return () =>
            window.removeEventListener('beforeunload', () => {
                setFrameUrl();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!iframeLoading && !isHidden) {
            setTimeout(() => {
                setDisplayed(true);
            }, 1000);
        }
    }, [iframeLoading, isHidden]);

    const isNewInscription = user.roles.includes('ROLE_NEW_INSCRIPTION');

    const reloadPage = () => {
        window.location.href = location.pathname;
    };

    const filteredMenu = (menus, user) => {
        const menuFiltered = menus.map((menu) => {
            if (menu.menuItems) {
                return {
                    ...menu,
                    menuItems: menu.menuItems.filter((item) => {
                        console.log(item.path);
                        if (user.num_adherent !== process.env.REACT_APP_SWITCH_DAT && item.path === '/arrets/home') {
                            return false;
                        }
                        if (user.num_adherent === process.env.REACT_APP_SWITCH_DAT && item.path === '/arrets/create') {
                            return false;
                        }
                        return true;
                    }),
                };
            }
            return menu;
        });

        return menuFiltered;
    };

    return (
        <>
            {process.env.REACT_APP_MAINTENANCE === '1' ? (
                <MaintenancePage />
            ) : (
                <>
                    {isNewInscription ? (
                        currentPage.path === '/utilisateurs/bienvenue' ? (
                            <UserWelcomePage />
                        ) : (
                            <UsersPageAdd mode={'newUser'} />
                        )
                    ) : token ? (
                        <Box display="flex" className="h-100">
                            <Sidebar menus={filteredMenu(menus, user)} onRedirect={handleRedirect} />
                            <Box display="flex" flexDirection="column" flexGrow="1" height="100%" maxWidth="100%">
                                <Header />

                                <Box flexGrow="1" display="flex" flexDirection="column" className={classnames(styles.layout__main)}>
                                    {isHidden && currentPage && !iframeLoading && (
                                        <Box style={{ marginBottom: '5px' }}>
                                            <SGSPageTitle title={currentPage.name} category={currentPage.category} description={currentPage.description} icon={currentPage.icon}></SGSPageTitle>
                                        </Box>
                                    )}
                                    {!isHidden && (
                                        <Box className={classnames(styles.layout__main__content, drawerOpen ? styles.drawer__open : styles.drawer__close)} style={{ marginBottom: '-20px' }}>
                                            <Box style={{ background: 'white' }} className={classnames(!displayed ? 'd-none' : '')}>
                                                <Box display="flex" flexDirection="row" className="pt-3 pl-5 pb-1">
                                                    <Box>{location.pathname === '/arrets/create' && <SGSButton onClick={reloadPage}>Accueil arrêt de travail</SGSButton>}</Box>
                                                    <Box id="loadingPage" className="mr-5" style={{ marginLeft: 'auto' }}>
                                                        <Box sx={{ display: 'flex' }} className="mt-0">
                                                            <CircularProgress disableShrink />
                                                            <Box className="mt-2 ml-2">
                                                                <span style={{ fontFamily: 'Open Sans bold' }}>Chargement...</span>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                    <Box flexGrow="1" className={classnames(styles.layout__main__content, drawerOpen ? styles.drawer__open : styles.drawer__close)}>
                                        <SGSHelpButton />
                                        {!isHidden && (
                                            <Box id="iframeContenainer" style={{ height: '100%' }}>
                                                <Box
                                                    id="iFrameLoader"
                                                    className={classnames(styles.layout__iframe, !displayed ? 'd-none' : '')}
                                                    style={{ background: 'white', display: 'none', marginBottom: '-100%' }}
                                                ></Box>
                                                <iframe
                                                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
                                                    id="iframe-sgsante"
                                                    className={classnames(styles.layout__iframe, !displayed ? 'd-none' : '')}
                                                    title="frame-sgsante"
                                                    src={link}
                                                    frameBorder="0"
                                                    onLoad={handleLoad}
                                                />
                                            </Box>
                                        )}
                                        {children}
                                    </Box>
                                </Box>
                                {token && <Footer />}
                            </Box>
                        </Box>
                    ) : (
                        <Box style={{ height: '100%' }}>{children}</Box>
                    )}
                    <AppLoader />
                    <NotificationQueue />
                </>
            )}
        </>
    );
};

export default Layout;
